import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, skip, switchMap, take, tap } from 'rxjs/operators';
import { DownLoadFileService } from '~app/services/download-file.service';
import { ClientNotification, ShowNotification } from '~app/shared/notifications';
import { LegalEntityPracticeCommandService } from './legalEntityPractice-command.service';
import { LegalEntityPracticeQueryService } from './legalEntityPractice-query.service';
import { DownloadAttachment, DownloadAttachmentFailure, DownloadAttachmentSuccess, LoadRegisterReport, LoadRegisterReportFailure, LoadRegisterReportSuccess } from './legalEntityPractice.action';
import { LegalEntityPracticeStateModel } from './legalEntityPractice.state.model';

@State<LegalEntityPracticeStateModel>({
  name: 'legalEntityPractice',
  defaults: LegalEntityPracticeStateModel.empty
})
@Injectable()
export class LegalEntityPracticeState {
  constructor(
    private store: Store,
    private commandService: LegalEntityPracticeCommandService,
    private queryService: LegalEntityPracticeQueryService,
    private downLoadFileService: DownLoadFileService,
    private translate: TranslateService
   ) {}

  @Selector()
  static loading({ loading }: LegalEntityPracticeStateModel): boolean {
    return loading;
  }

  @Action(LoadRegisterReport)
  loadRegisterReport(
    ctx: StateContext<LegalEntityPracticeStateModel>,
    { legalEntityId }: LoadRegisterReport
  ) {
    this.store.dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(`legalEntityPractice.notification.downloadLegalEntityRegisterReport`),
        )
      )
    );

    ctx.patchState({ loading: true });
    return this.commandService.genereateLegalEntityRegisterReport(legalEntityId).pipe(
      tap((_) => this.store.dispatch(new LoadRegisterReportSuccess(legalEntityId))),
      catchError((error) => this.store.dispatch(new LoadRegisterReportFailure(error)))
    );
  }

  @Action(LoadRegisterReportSuccess)
  loadRegisterReportSuccess(
    ctx: StateContext<LegalEntityPracticeStateModel>,
    {legalEntityId}: LoadRegisterReportSuccess
  ) {

    return this.queryService.getPdfExport(legalEntityId).pipe(
      skip(1),
      take(1),
      tap((pdfExport) => ctx.dispatch(new DownloadAttachment(pdfExport, legalEntityId))),
      catchError((error) => ctx.dispatch(new DownloadAttachmentFailure(error)))
    );
  }

  @Action(LoadRegisterReportFailure)
  loadRegisterReportFailure(ctx: StateContext<LegalEntityPracticeStateModel>, { error }: any) {
    ctx.patchState({ loading: false });
  }

  @Action(DownloadAttachment)
  downloadAttachment(
    { patchState }: StateContext<LegalEntityPracticeStateModel>,
    { attachment, legalEntityId }: DownloadAttachment
  ) {

    patchState({
      loading: false
    });

    if (!attachment) {
      this.store.dispatch(new DownloadAttachmentFailure('No document to download'));
    }
    if (!attachment.documentId) {
      const uri = ((attachment as any).downloadLink as string).split('/');
      attachment = { ...attachment, documentId: uri[uri.length - 1] };
    }

    return this.commandService
      .authorizeDocumentDownload(legalEntityId, attachment.documentId)
      .pipe(
        switchMap((location) => this.downLoadFileService.downloadFile(location, attachment.fileName)),
        tap(() => this.store.dispatch(new DownloadAttachmentSuccess())),
        catchError((error) => this.store.dispatch(new DownloadAttachmentFailure(error)))
      );
  }
}
